import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AmplifierComponentTreeView from './AmplifierComponentTreeView';
import safeStorage from '../../utils/safeStorage';

// API URL configuration - uses relative URL in production (nginx proxies to backend)
const API_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'localhost' && window.location.port === '3002'
    ? 'http://localhost:3000/api'
    : '/api');

const templateComponentsApi = axios.create({ baseURL: API_URL });
templateComponentsApi.interceptors.request.use(config => {
  const token = safeStorage.getItem('authToken');
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  return config;
});

const AmplifierTemplateComponents = ({ amplifierId, readOnly = false }) => {
  const [templateStructure, setTemplateStructure] = useState([]);
  const [availableComponents, setAvailableComponents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasTemplate, setHasTemplate] = useState(false);
  const [selectedPartType, setSelectedPartType] = useState(null);
  const [showComponentSelector, setShowComponentSelector] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  // Fetch template structure and assigned components
  const fetchTemplateStructure = useCallback(async () => {
    if (!amplifierId) return;
    setLoading(true);
    try {
      const response = await templateComponentsApi.get(`/amplifier_template_structure/${amplifierId}`);
      setTemplateStructure(response.data);
      // If we get any data back, the amplifier has a template
      // Empty array means no template assigned, any data means template exists
      setHasTemplate(response.data.length > 0);
    } catch (error) {
      console.error('Error fetching template structure:', error);
      setTemplateStructure([]);
      setHasTemplate(false);
    }
    setLoading(false);
  }, [amplifierId]);

  // Fetch available components
  const fetchAvailableComponents = useCallback(async () => {
    try {
      const response = await templateComponentsApi.get('/available_components');
      setAvailableComponents(response.data);
    } catch (error) {
      console.error('Error fetching available components:', error);
      setAvailableComponents([]);
    }
  }, []);

  useEffect(() => {
    fetchTemplateStructure();
    fetchAvailableComponents();
  }, [amplifierId, fetchTemplateStructure, fetchAvailableComponents]);

  // Group template structure by section
  const groupedStructure = templateStructure.reduce((acc, item) => {
    const sectionKey = `${item.section_id}-${item.section_name}`;
    if (!acc[sectionKey]) {
      acc[sectionKey] = {
        section_id: item.section_id,
        section_name: item.section_name,
        section_order: item.section_order,
        part_types: []
      };
    }
    acc[sectionKey].part_types.push(item);
    return acc;
  }, {});

  const sections = Object.values(groupedStructure).sort((a, b) => a.section_order - b.section_order);

  // Handle adding component to part type
  const handleAddComponent = async () => {
    if (readOnly) return;
    if (!selectedComponent || !selectedPartType) return;
    
    try {
      await templateComponentsApi.post('/amplifier_template_components', {
        amplifier_id: amplifierId,
        part_type_id: selectedPartType.part_type_id,
        component_id: selectedComponent,
        quantity,
        notes
      });
      
      // Reset form and refresh data
      setSelectedComponent('');
      setQuantity(1);
      setNotes('');
      setShowComponentSelector(false);
      setSelectedPartType(null);
      fetchTemplateStructure();
    } catch (error) {
      console.error('Error adding component:', error);
      alert('Failed to add component. Please try again.');
    }
  };

  // Handle removing component
  const handleRemoveComponent = async (componentAssignmentId) => {
    if (readOnly) return;
    if (!window.confirm('Are you sure you want to remove this component assignment?')) return;
    
    try {
      await templateComponentsApi.delete(`/amplifier_template_components/${componentAssignmentId}`);
      fetchTemplateStructure();
    } catch (error) {
      console.error('Error removing component:', error);
      alert('Failed to remove component. Please try again.');
    }
  };

  // Handle updating quantity/notes
  const handleUpdateComponent = async (componentAssignmentId, newQuantity, newNotes) => {
    if (readOnly) return;
    try {
      await templateComponentsApi.put(`/amplifier_template_components/${componentAssignmentId}`, {
        quantity: newQuantity,
        notes: newNotes
      });
      fetchTemplateStructure();
    } catch (error) {
      console.error('Error updating component:', error);
      alert('Failed to update component. Please try again.');
    }
  };

  // Filter components based on search term
  const filteredComponents = availableComponents.filter(component =>
    component.component_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    component.manufacturer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    component.category_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!amplifierId) {
    return (
      <div style={{ padding: '1rem', textAlign: 'center', color: '#666' }}>
        Please select an amplifier to view template-based component assignments.
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: '1rem', textAlign: 'center' }}>
        Loading template structure...
      </div>
    );
  }

  if (!hasTemplate) {
    return (
      <div style={{ 
        background: '#f0f0f0',
        border: '1px solid #e9ecef',
        borderRadius: '12px',
        padding: '2rem',
        textAlign: 'center',
        color: '#555',
        fontSize: '1.1rem'
      }}>
        This amplifier does not have a template assigned. Please assign a template to the amplifier to use template-based component assignment.
      </div>
    );
  }

  return (
    <div>
      {readOnly ? (
        // Report-style display for read-only mode
        <div>
          <h3 style={{ 
            margin: '0 0 2rem 0', 
            color: '#333',
            fontSize: '1.5rem',
            fontWeight: 'bold',
            textShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
            textAlign: 'center'
          }}>
            Component Report
          </h3>
        </div>
      ) : (
        // Original interactive mode header
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h3 style={{ 
              margin: 0, 
              color: '#333',
            fontSize: '1.5rem',
              fontWeight: 'bold',
              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)'
            }}>
              Template-Based Component Assignment
            </h3>
            <button
              onClick={() => setShowPreview(true)}
              style={{
                background: '#17a2b8',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                padding: '0.75rem 1.5rem',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 'bold',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
                transition: 'all 0.3s ease',
                transform: 'translateY(0)'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.2)';
              }}
            >
              Preview
            </button>
          </div>
        </>
      )}
      
      <div>
        {sections.map(section => (
          <div key={section.section_id} style={{ 
            marginBottom: '2rem', 
            background: 'white',
            borderRadius: '8px',
            padding: '2rem',
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e9ecef'
          }}>
            <h4 style={{ 
              margin: '0 0 1.5rem 0', 
              color: '#333',
              fontSize: '1.4rem',
              fontWeight: 'bold',
              borderBottom: '3px solid #2563eb',
              paddingBottom: '0.75rem',
              background: '#2563eb',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              {section.section_name}
            </h4>
            
            {readOnly ? (
              // Clean report format - just components under section headers
              <div style={{ marginBottom: '2rem' }}>
                {(() => {
                  // Collect all components from all part types in this section
                  const allComponents = section.part_types.reduce((acc, partType) => {
                    if (partType.assigned_components && partType.assigned_components.length > 0) {
                      acc.push(...partType.assigned_components);
                    }
                    return acc;
                  }, []);
                  
                  // Don't show section if no components
                  if (allComponents.length === 0) return null;
                  
                  return (
                    <div style={{ 
                      background: 'white',
                      border: '1px solid rgba(37, 99, 235, 0.1)',
                      borderRadius: '12px',
                      padding: '1.5rem',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                    }}>
                      <ul style={{ 
                        margin: 0,
                        padding: 0,
                        listStyle: 'none'
                      }}>
                        {allComponents.map((component) => (
                          <li key={component.amplifier_template_component_id} style={{ 
                            fontSize: '1rem',
                            color: '#333',
                            lineHeight: '1.4',
                            marginBottom: '0.25rem',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '0.5rem'
                          }}>
                            <span style={{ 
                              color: '#2563eb',
                              fontSize: '1rem',
                              marginTop: '0.1rem'
                            }}>
                              •
                            </span>
                            <span style={{ 
                              fontWeight: 'bold',
                              color: '#2563eb',
                              minWidth: '30px'
                            }}>
                              {component.quantity}×
                            </span>
                            <span style={{ 
                              fontWeight: '500',
                              color: '#333',
                              flex: 1
                            }}>
                              {component.manufacturer_name} {component.component_name}
                              {component.notes && (
                                <span style={{ 
                                  fontStyle: 'italic', 
                                  color: '#666',
                                  fontWeight: 'normal',
                                  marginLeft: '0.5rem'
                                }}>
                                  ({component.notes})
                                </span>
                              )}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })()}
              </div>
            ) : (
              // Original interactive mode for editing
              <div>
                {section.part_types.map(partType => (
                  <div key={partType.part_type_id} style={{ 
                    marginBottom: '1.5rem', 
                    background: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    padding: '1.5rem',
                    borderRadius: '12px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                      <span style={{ 
                        fontWeight: 'bold', 
                        color: '#333',
                        fontSize: '1.1rem'
                      }}>
                        {partType.part_type_name}
                      </span>
                      <button
                        onClick={() => {
                          setSelectedPartType(partType.part_type_id);
                          setShowComponentSelector(true);
                          fetchAvailableComponents();
                        }}
                        style={{
                          padding: '0.25rem 0.5rem',
                          backgroundColor: '#007bff',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.9rem'
                        }}
                      >
                        + Add Component
                      </button>
                    </div>
                    
                    <div style={{ marginTop: '0.5rem' }}>
                      {partType.assigned_components && partType.assigned_components.length > 0 ? (
                        <div style={{ display: 'grid', gap: '0.5rem' }}>
                          {partType.assigned_components.map(component => (
                            <div key={component.amplifier_template_component_id} 
                                 style={{ 
                                   display: 'flex', 
                                   justifyContent: 'space-between', 
                                   alignItems: 'center',
                                   padding: '0.5rem',
                                   backgroundColor: 'white',
                                   border: '1px solid #ddd',
                                   borderRadius: '4px'
                                 }}>
                              <div style={{ flex: 1 }}>
                                <strong>{component.component_name}</strong> 
                                <span style={{ color: '#666', marginLeft: '0.5rem' }}>
                                  ({component.manufacturer_name})
                                </span>
                                <div style={{ fontSize: '0.9rem', color: '#888' }}>
                                  {component.category_name} &gt; {component.subcategory_name}
                                </div>
                                {component.notes && (
                                  <div style={{ fontSize: '0.8rem', fontStyle: 'italic', color: '#666', marginTop: '0.25rem' }}>
                                    Notes: {component.notes}
                                  </div>
                                )}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <label style={{ fontSize: '0.9rem' }}>Qty:</label>
                                <input
                                  type="number"
                                  value={component.quantity}
                                  onChange={(e) => {
                                    const newQuantity = parseInt(e.target.value) || 1;
                                    handleUpdateComponent(component.amplifier_template_component_id, newQuantity, component.notes);
                                  }}
                                  style={{ width: '50px', padding: '0.25rem' }}
                                  min="1"
                                />
                                <button
                                  onClick={() => handleRemoveComponent(component.amplifier_template_component_id)}
                                  style={{
                                    padding: '0.25rem 0.5rem',
                                    backgroundColor: '#dc3545',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '0.8rem'
                                  }}
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ color: '#888', fontStyle: 'italic' }}>
                          No components assigned to this part type yet. Click "Add Component" to get started.
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Component Selector Modal */}
      {showComponentSelector && selectedPartType && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            maxWidth: '600px',
            maxHeight: '80vh',
            overflow: 'auto',
            width: '90%'
          }}>
            <h4>Add Component to: {selectedPartType.part_type_name}</h4>
            
            <div style={{ marginBottom: '1rem' }}>
              <label>Search Components:</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, manufacturer, or category..."
                style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
              />
            </div>
            
            <div style={{ marginBottom: '1rem' }}>
              <label>Select Component:</label>
              <select
                value={selectedComponent}
                onChange={(e) => setSelectedComponent(e.target.value)}
                style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
              >
                <option value="">Choose a component...</option>
                {filteredComponents.map(component => (
                  <option key={component.component_id} value={component.component_id}>
                    {component.component_name} - {component.manufacturer_name} ({component.category_name})
                  </option>
                ))}
              </select>
            </div>
            
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
              <div style={{ flex: 1 }}>
                <label>Quantity:</label>
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                  min="1"
                  style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
                />
              </div>
            </div>
            
            <div style={{ marginBottom: '1rem' }}>
              <label>Notes (optional):</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes about this component assignment..."
                style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem', minHeight: '60px' }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowComponentSelector(false);
                  setSelectedPartType(null);
                  setSelectedComponent('');
                  setQuantity(1);
                  setNotes('');
                  setSearchTerm('');
                }}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddComponent}
                disabled={!selectedComponent}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: selectedComponent ? '#28a745' : '#e2e8f0',
                  color: selectedComponent ? 'white' : '#999',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: selectedComponent ? 'pointer' : 'not-allowed'
                }}
              >
                Add Component
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && (
        <AmplifierComponentTreeView
          amplifierId={amplifierId}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
};

export default AmplifierTemplateComponents;
